import { Set, Map, List } from 'immutable';
import { signOutAction } from '@/domain/env/envActions';
import { ChatFactory } from '@/domain/chat/helpers';
import * as A from '@/domain/chat/chatActions';
import * as Legacy from '@/domain/chat/legacy/chatActions';

const mergeMessages = (messages, newMessages) => {
  const existingMessageIds = messages.map((m) => m.messageId);
  const uniqNewMessages = newMessages.filter((m) => !existingMessageIds.includes(m.messageId));
  return messages.concat(uniqNewMessages);
};

function mergeThread(oldValue, newValue, key) {
  return key === 'messages' ? mergeMessages(oldValue, newValue) : newValue;
}

export const reducer = {
  chat(state = ChatFactory(), action) {
    switch (action.type) {
      // ========================
      // Shared actions (both v3 and v4)
      // ========================
      case A.fetchChatUsers.success:
      case A.fetchDocumentChatUsers.success:
        return state.set('users', action.payload);

      case A.fetchCompanyChatUsers.success:
        return state.set('users', action.payload).set('usersCompanyId', action.companyId ?? '');

      case A.chatResetUsersAction.type:
        return state.set('users', List());

      case A.onInitOneSignalAction.type:
        return state.set('isOneSignalInit', true);

      case A.setActiveChannelAction.type:
        return state.set('activeChannel', action.payload.name);

      case A.setActiveChannelForceAction.type:
        return state.set('activeChannel', action.payload.name).set('activeChannelForce', action.payload.name);

      case A.removeActiveChannelForceAction.type:
        return state.set('activeChannelForce', null);

      case A.chatReadyToRenderAction.type:
        return state.set('readyToRenderOnDocument', action.payload);

      case A.setOpenAction.type:
        return state
          .set('isOpen', action.payload)
          .set('channelUrl', '')
          .set('targetTimestamp', null)
          .set('targetMessageId', null);

      case A.setChannelUrlAction.type:
        return state.set('channelUrl', action.payload);

      case A.setTargetMessageAction.type:
        return state.set('targetTimestamp', action.payload.timestamp).set('targetMessageId', action.payload.id);

      // ========================
      // V4-only actions (modern chat)
      // ========================
      case A.setUnreadDocumentsAction.type:
        return state.set('unreadDocuments', action.payload);

      case A.setUnreadCompaniesAction.type:
        return state.set('unreadCompanies', action.payload);

      case A.setUnreadFromChannelsAction.type:
        return state
          .set('unreadCompanies', action.payload.unreadCompanies)
          .set('unreadDocuments', action.payload.unreadDocuments)
          .set('totalUnreadCount', action.payload.totalUnreadCount);

      // PubSub CHAT_UNREAD_UPDATE (closed drawer): set ONLY the authoritative total.
      // The per-company/document maps are deliberately left as-is — the event carries
      // no authoritative values for them (updating them would need banned delta math).
      case A.setTotalUnreadFromWsAction.type:
        return state.set('totalUnreadCount', action.payload);

      // ========================
      // V3-only actions (legacy chat)
      // ========================
      case Legacy.chatConnectAction.success:
        return state.set('connectionStatus', 'CONNECTED');

      case Legacy.chatGetThreadsAction.success:
      case Legacy.chatGetWorkerThreadsAction.success:
        return state
          .update('threads', (t) =>
            t.mergeDeep(action.payload).map((i) =>
              i
                .update('members', (m) =>
                  m
                    .groupBy((x) => x.userId)
                    .map((x) => x.first())
                    .toList(),
                )
                .update('messages', (m) =>
                  m
                    .groupBy((x) => x.messageId)
                    .map((x) => x.first())
                    .toList(),
                ),
            ),
          )
          .set('loaded', true);

      case Legacy.createChannelAction.success:
        return state
          .setIn(['messageQuery', action.payload.name], action.payload.messageQuery)
          .setIn(['threads', action.payload.name], action.payload.messages)
          .setIn(['channels', action.payload.name], action.payload.channel);

      case Legacy.sendMessageAction.success:
        return state.updateIn(['threads', action.payload.name], (u) =>
          u ? u.mergeWith(mergeThread, action.payload) : action.payload,
        );

      case Legacy.markChannelAsReadAction.type:
        return state.setIn(['threads', action.payload.name, 'unreadMessageCount'], 0);

      case Legacy.receiveMessageAction.success:
        return state.updateIn(['threads', action.payload.name], (u) =>
          u ? u.mergeWith(mergeThread, action.payload) : action.payload,
        );

      case Legacy.documentAddThreadAction.type:
        return state
          .updateIn(['company', action.payload.companyId], (c) => (c || new Set()).add(action.payload.name))
          .updateIn(['threads', action.payload.name], (l) => (l || new Set()).add(action.payload.message))
          .setIn(['channels', action.payload.name], action.payload.channel);

      case Legacy.getPreviousMessageAction.success:
        return state
          .updateIn(['threads', action.payload.name], (u) =>
            u ? u.mergeWith(mergeThread, action.payload) : action.payload,
          )
          .setIn(['messageQuery', action.payload.name], action.query);

      case Legacy.getMessagesAction.success:
        return state
          .updateIn(['threads', action.payload.name], (u) =>
            u ? u.mergeWith(mergeThread, action.payload) : action.payload,
          )
          .setIn(['threads', action.payload.name, 'unreadMessageCount'], 0)
          .setIn(['messageQuery', action.payload.name], action.query);

      case Legacy.getMessagesAction.failure:
        return state.setIn(['messageQuery', action.payload], Map({ isLoading: false }));

      case Legacy.setMessageHandlerIdAction.type:
        return state.set('messageHandlerId', action.payload.id);

      case Legacy.setChatConnectionStatusAction.type:
        return state.set('connectionStatus', action.payload);

      case Legacy.terminateChannelsAction.success:
        return state
          .update('threads', (u) => u.removeAll(action.payload))
          .update('activeChannel', (u) => (action.payload.includes(u) ? null : u))
          .update('messageQuery', (u) => u.removeAll(action.payload));

      case Legacy.setAllowedChatWorkerAction.type:
        return state.set('allowedWorker', action.payload);

      case Legacy.setOnPageAction.type:
        return state.set('isOnPage', action.payload);

      // ========================
      // Sign out (both versions)
      // ========================
      case signOutAction.success:
        return (
          ChatFactory()
            // after signOut oneSignal remains initialized
            .set('isOneSignalInit', true)
        );

      default:
        return state;
    }
  },
};
